/** Pole pro řazení / hledání osob podle příjmení. */
export type PersonNameFields = {
  name?: string | null
  first_name?: string | null
  last_name?: string | null
}

const LEADING_TITLE_RE =
  /^(ing\.|mgr\.|bc\.|bcA\.|phdr\.|mudr\.|paeddr\.|judr\.|rndr\.|mvdr\.|ph\.d\.|csc\.|doc\.|prof\.)\s+/i

function normalizeKey(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
}

/**
 * Klíč pro abecední řazení: příjmení, pak jméno.
 * Preferuje last_name/first_name; u Azure jmen „Jan Novák“ bere poslední slovo jako příjmení.
 */
export function personSortKey(p: PersonNameFields): string {
  const ln = (p.last_name || '').trim()
  const fn = (p.first_name || '').trim()
  if (ln) {
    return normalizeKey(`${ln}\u0000${fn}`)
  }

  let name = (p.name || '').trim()
  // „Novák Jan, Ing.“ → formát školy (příjmení první); bez čárky spíš Azure „Jan Novák“
  const comma = name.indexOf(',')
  const hadTrailingTitles = comma > 0
  if (hadTrailingTitles) {
    name = name.slice(0, comma).trim()
  }
  // Odstranit všechny vedoucí tituly (Ing. Mgr. …), ať řazení není podle titulu/jména
  while (LEADING_TITLE_RE.test(name)) {
    name = name.replace(LEADING_TITLE_RE, '').trim()
  }
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    if (hadTrailingTitles) {
      return normalizeKey(`${parts[0]}\u0000${parts.slice(1).join(' ')}`)
    }
    const last = parts[parts.length - 1]
    const first = parts.slice(0, -1).join(' ')
    return normalizeKey(`${last}\u0000${first}`)
  }
  return normalizeKey(name)
}

export function comparePersonNames(a: PersonNameFields, b: PersonNameFields): number {
  return personSortKey(a).localeCompare(personSortKey(b), 'cs', { sensitivity: 'base' })
}

export function sortByPersonName<T extends PersonNameFields>(list: T[]): T[] {
  return [...list].sort(comparePersonNames)
}

/** Priorita shody při hledání: začátek příjmení > začátek jména > ostatní. */
export function personSearchRank(p: PersonNameFields, query: string): number {
  const q = normalizeKey(query)
  if (!q) return 0
  const ln = normalizeKey(p.last_name || '')
  const fn = normalizeKey(p.first_name || '')
  const name = normalizeKey(p.name || '')
  if (ln && ln.startsWith(q)) return 0
  if (name.startsWith(q)) return 1
  if (fn && fn.startsWith(q)) return 2
  if (ln && ln.includes(q)) return 3
  if (name.includes(q)) return 4
  return 5
}
